import { Component, effect, inject, input } from '@angular/core';
import { Observable, map } from 'rxjs';

import { EChartsOption } from 'echarts';
import { NgxEchartsDirective, provideEcharts } from 'ngx-echarts';

import { RegisterComponent } from '../../components/component-lookup';
import { GlobalConstants } from '../../../shared/global-constants';
import { VisualBaseComponent } from '../visual-base.component';
import { Peer, StateService, SubjectType } from '../../services/state.service';
import { VisualService } from '../../services/visual.service';
import { ChartInfoBoxComponent } from '../chart-info-box/chart-info-box.component';

interface MetricData {
  name: string,
  value: number,
  type: string,
  color?: string,
  displayType?: string
}

@RegisterComponent('BarNetChart')
@Component({
  selector: 'app-bar-net-chart',
  standalone: true,
  imports: [NgxEchartsDirective, ChartInfoBoxComponent],
  templateUrl: './bar-net-chart.component.html',
  styleUrl: './bar-net-chart.component.scss',
  providers: [provideEcharts()]
})
export class BarNetChartComponent extends VisualBaseComponent {
  elementId = input.required<string>();
  subjectType = input.required<SubjectType>();

  readonly institution = inject(StateService).institution;
  readonly peer = inject(StateService).peer;
  readonly filteredGroup = inject(StateService).filteredGroup;
  readonly dateRange = inject(StateService).dateRange;

  override loadingColor = '#ebebeb';
  override initOptions = { height: 100 }
  override baseOptions: EChartsOption = {
    grid: {
      left: 10,
      right: 150,
      top: 10,
      bottom: 10,
      containLabel: true
    },
    legend: {
      show: true,
      orient: 'vertical',
      left: 'right',
      top: 25,
    },
    xAxis: {
      type: 'value',
      show: true,
      axisLabel: {
        color: '#243b5b',
        // formatter: function (value: number) {
        //   return value.toFixed(2).toLocaleString() + '%';
        // }
      },
      splitLine: {
        lineStyle: {
          color: '#ccc'
        }
      }
    },
    yAxis: {
      type: 'category',
      show: false,
      data: []
    },
    series: [
      {
        name: 'Net',
        type: 'bar',
        label: {
          show: true,
          position: 'right',
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          // formatter: function (params :any) {
          //   return params.value == 0 ? '' : params.value.toFixed(3) == 0 ? '< 0.001%' : VisualService.getValueDisplay(params.value, 'PCT3');
          // }
        },
        itemStyle: {
          color: GlobalConstants.colors[0]
        },
        data: []
      },
      {
        name: 'Recoveries',
        type: 'bar',
        label: {
          show: true,
          position: 'left',
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          // formatter: function (params: any) {
          //   return params.value == 0 ? '' : params.value.toFixed(3) == 0 ? '< 0.001%' : VisualService.getValueDisplay(params.value, 'PCT3');
          // }
        },
        itemStyle: {
          color: GlobalConstants.scoreColors[4]
        },
        data: []
      },
      {
        name: 'Charge Offs',
        type: 'bar',
        label: {
          show: true,
          position: 'right',
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          // formatter: function (params: any) {
          //   return params.value == 0 ? '' : params.value.toFixed(3) == 0 ? '< 0.001%' : VisualService.getValueDisplay(params.value, 'PCT3');
          // }
        },
        itemStyle: {
          color: GlobalConstants.scoreColors[2]
        },
        data: []
      }
    ]
  };

  constructor() {
    super();

    effect(() => {
      const subjectType = this.subjectType();
      const peer = subjectType == SubjectType.peer ? this.peer() : undefined;
      const date = this.dateRange()?.endDate;
      const elementId = this.elementId();
      const group = this.filteredGroup();
      if (this.chartInit() && subjectType && (peer || subjectType == SubjectType.institution) && date && elementId && group) {
        this.updateVisual(peer, date, elementId);
      }
    });
  }

  public updateVisual(peer: Peer | undefined, date: Date, elementId: string) {
    this.processMetricData(this.subjectType(), peer, date, elementId).subscribe(metricData => {
      const companionType = this.subjectType() == SubjectType.institution ? SubjectType.peer : SubjectType.institution;
      const companion = this.subjectType() == SubjectType.institution ? this.peer() : undefined;
      this.processMetricData(companionType, companion, date, elementId).subscribe(companionData => {
        const maxMinusValue = this.getMaxValue(metricData, companionData, 'MINUS');
        const maxPlusValue = this.getMaxValue(metricData, companionData, 'PLUS');

        const min = (Math.ceil(maxMinusValue * 10)) / 10;
        const max = (Math.ceil(maxPlusValue * 10)) / 10;

        if (metricData.length == 0) {
          this.echartsInstance?.clear();
          this.chartOptions = this.noDataOptions;
        }
        else {
          this.updateOptions = {
            tooltip: {
              trigger: 'axis',
              formatter: function (params: { name: string, color: string; seriesName: string; seriesType: string, value: number; }[]) {
                let tooltip: string = '<div style="margin: 0px 0 0;line-height:1;"><div style="margin: 0px 0 0;line-height:1;"><div style="font-size:14px;color:#666;font-weight:400;line-height:1;">' + params[0].name + '</div><div style="margin: 10px 0 0;line-height:1;">';
                params.forEach((param: { color: string; seriesName: string; value: number; }) => {
                  if (param.value != undefined) {
                     const paramValue = VisualService.getValueDisplay(param.value, metricData[0].displayType);
                    tooltip += '<div style="margin: 10px 0 0;line-height:1;"><div style="margin: 0px 0 0;line-height:1;"><span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color: ' + param.color + ';"></span><span style="font-size:14px;color:#666;font-weight:400;margin-left:2px">' + param.seriesName + '</span><span style="float:right;margin-left:20px;font-size:14px;color:#666;font-weight:900">' + paramValue + '%</span><div style="clear:both"></div></div><div style="clear:both"></div></div>'
                  }
                });
                tooltip += '</div></div></div>'
                return tooltip;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              } as any
            },
            yAxis: {
              data: metricData.filter(m => m.type == 'NET').map(m => m.name).reverse(),
            },
            xAxis: {
              min: min % 0.2 == 0 ? min * -1 : (min + 0.1) * -1,
              max: max % 0.2 == 0 ? max : max + 0.1,
              axisLabel: {
                formatter: function (value: number) {
                  return VisualService.getValueDisplay(value, metricData[0].displayType);
                }
              }
            },
            series: [
              {
                //name: 'Net',
                name: metricData.find(m => m.type == 'NET')?.name,
                data: metricData.reverse().filter(m => m.type == 'NET').map(m => m.value),
                itemStyle: {
                  color: metricData.find(m => m.type == 'NET')?.color ?? GlobalConstants.colors[0]
                },
                label: {
                  formatter: function (params: any) {
                    return params.value == 0 ? '' : params.value.toFixed(3) == 0 ? '< 0.001%' : VisualService.getValueDisplay(params.value, metricData[0].displayType);
                  }
                }
              },
              {
                //name: 'Recoveries',
                name: metricData.find(m => m.type == 'MINUS')?.name,
                data: metricData.reverse().filter(m => m.type == 'MINUS').map(m => m.value * -1),
                itemStyle: {
                  color: metricData.find(m => m.type == 'MINUS')?.color ?? GlobalConstants.scoreColors[4]
                },
                label: {
                  formatter: function (params: any) {
                    return params.value == 0 ? '' : params.value.toFixed(3) == 0 ? '< 0.001%' : VisualService.getValueDisplay(params.value, metricData[0].displayType);
                  }
                }
              },
              {
                //name: 'Charge Offs',
                name: metricData.find(m => m.type == 'PLUS')?.name,
                data: metricData.reverse().filter(m => m.type == 'PLUS').map(m => m.value),
                itemStyle: {
                  color: metricData.find(m => m.type == 'PLUS')?.color ?? GlobalConstants.scoreColors[2]
                },
                label: {
                  formatter: function (params: any) {
                    return params.value == 0 ? '' : params.value.toFixed(3) == 0 ? '< 0.001%' : VisualService.getValueDisplay(params.value, metricData[0].displayType);
                  }
                }
              }
            ]
          };
        }
        if (!this.echartsInstance?.isDisposed()) {
          this.echartsInstance?.hideLoading();
        }
      });
    });
  }

  processMetricData(subjectType: SubjectType, peer: Peer | undefined, date: Date, elementId: string): Observable<MetricData[]> {
    return this.metricService.getElementData(this.institution().iid, elementId, peer).pipe(map(metrics => {
      const metricData: MetricData[] = [];
      let index = 0;
      metrics.forEach((metric) => {
        const dataRow = metric.data.findIndex((r) => { return new Date(r[0]).getTime() == date.getTime(); });

        let indexes: number[] = [];
        if (subjectType == SubjectType.peer) {
          indexes = this.visualService.getPeerIndexes(metric.data[0]);
        } else {
          indexes.push(metric.data[0].findIndex((c: number) => {
            return c == this.institution().iid;
          }));
        }

        let sum = 0; let count = 0;
        indexes.forEach(i => {
          if (dataRow > -1 && metric.data[dataRow][i] != null) {
            sum += metric.data[dataRow][i]; count++;
          }
        });
        const value: number = count > 0 ? sum / count : 0;
        const valueFinal = VisualService.getValue(value, metric.displayType)

        const color: string | undefined = this.chartColors().find(c => c.metricId == metric.metricId)?.color;
        metricData[index++] = { name: metric.rowTitle, value: valueFinal ?? 0, type: metric.compType, color: color, displayType: metric.displayType };
      });

      return metricData;
    }));
  }

  getMaxValue(metricData: MetricData[], companionData: MetricData[], type: string): number {
    const values = metricData.filter(m => m.type == type).map(m => m.value).concat(companionData.filter(m => m.type == type).map(m => m.value));
    return Math.max(...values);
  }
}
